"use client";

import React, { useMemo } from "react";
import { ArrowRight, Flame } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Link } from "@/components/common/Link";
import { decodeEntities } from "@/lib/decodeEntities";
import {
  EcommerceProductsProvider,
  useEcommerceProducts,
  ProductPrice,
} from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import type { EcommerceProduct } from "@/integrations/wordpress/types";

function RelatedProductsList({ currentProductId }: { currentProductId: string | number }) {
  const { posts: products, loading, error } = useEcommerceProducts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {[0, 1, 2, 3].map((idx) => (
          <div
            key={idx}
            data-index={idx}
            className="bg-card border border-border rounded-md p-4 flex flex-col justify-between h-80"
          >
            <Skeleton className="w-full h-44 rounded-md mb-4" />
            <div className="space-y-2">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-1/2" />
            </div>
            <Skeleton className="h-8 w-full mt-4" />
          </div>
        ))}
      </div>
    );
  }

  if (error || !products) {
    return null;
  }

  // Filter out the current product being viewed
  const related = products.filter((p) => String(p.id) !== String(currentProductId)).slice(0, 4);

  if (related.length === 0) {
    return null;
  }

  return (
    <div className="mt-16 md:mt-24 pt-12 border-t border-border">
      <div className="flex items-center justify-between gap-4 mb-8">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-foreground font-serif">
            Related Gas Water Heaters
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Other residential models in this category.
          </p>
        </div>
        <Button asChild variant="outline" size="sm" className="hidden sm:inline-flex">
          <Link to="/shop" className="inline-flex items-center gap-1.5">
            <span>View all models</span>
            <ArrowRight className="size-3.5" />
          </Link>
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {related.map((item, idx) => {
          const itemImg =
            item.featured_image_url ||
            (item.gallery_image_urls && item.gallery_image_urls[0]) ||
            "";
          const itemUrl = item.link || "/shop";
          const itemTitle = decodeEntities(item.title);
          const categoryName = decodeEntities(item.categories?.[0]?.name || "Natural Gas");

          return (
            <Card
              key={item.id}
              data-index={idx}
              className="bg-card text-card-foreground border border-border rounded-md shadow-2xs hover:shadow-md transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 flex flex-col justify-between overflow-hidden group"
            >
              <CardContent className="p-0 flex flex-col h-full">
                <div className="relative aspect-square w-full bg-muted/20 border-b border-border overflow-hidden flex items-center justify-center p-4">
                  {itemImg ? (
                    <img
                      src={itemImg}
                      alt={itemTitle}
                      loading="lazy"
                      className="max-h-full max-w-full object-contain transition-transform duration-300 group-hover:scale-102"
                    />
                  ) : (
                    <div className="flex flex-col items-center justify-center text-muted-foreground gap-2">
                      <Flame className="size-10 text-primary/40" />
                      <span className="text-xs uppercase tracking-wider font-medium">AquaFlame Unit</span>
                    </div>
                  )}
                  {item.on_sale && (
                    <span className="absolute top-2.5 left-2.5 px-2 py-0.5 rounded-md bg-secondary text-secondary-foreground text-[10px] font-bold uppercase tracking-wider">
                      Sale
                    </span>
                  )}
                </div>

                <div className="p-4 flex flex-col flex-grow justify-between gap-3">
                  <div>
                    <span className="text-[11px] font-semibold text-primary uppercase tracking-wider line-clamp-1">
                      {categoryName}
                    </span>
                    <h3 className="font-serif font-bold text-sm sm:text-base text-foreground leading-snug line-clamp-2 mt-1">
                      <Link to={itemUrl} className="hover:text-primary transition-colors">
                        {itemTitle}
                      </Link>
                    </h3>
                  </div>

                  <div className="pt-3 border-t border-border flex items-center justify-between gap-2 mt-auto">
                    <ProductPrice
                      product={item}
                      className="text-base font-bold text-foreground font-serif"
                    />
                    <Button asChild size="sm" variant="secondary" className="h-8 text-xs font-semibold px-2.5">
                      <Link to={itemUrl} className="inline-flex items-center gap-1">
                        <span>Details</span>
                        <ArrowRight className="size-3" />
                      </Link>
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

interface RelatedProductsProps {
  product: EcommerceProduct;
}

export function RelatedProducts({ product }: RelatedProductsProps) {
  const relatedQuery = useMemo(() => {
    if (!product) return null;
    const catSlug = product.categories?.[0]?.slug;
    const vars: Record<string, any> = {
      post_type: "product",
      per_page: 5,
    };
    if (catSlug && catSlug !== "uncategorized" && catSlug !== "warranty-plans") {
      vars.product_cat = catSlug;
    }
    return new WP_Query(vars);
  }, [product]);

  if (!relatedQuery) {
    return null;
  }

  return (
    <EcommerceProductsProvider wp_query={relatedQuery}>
      <RelatedProductsList currentProductId={product.id} />
    </EcommerceProductsProvider>
  );
}
